import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { NamedLink, ResponsiveImage, WishlistHeart } from '../../../components';
import { useConfiguration } from '../../../context/configurationContext';
import { updateWishlist, WISHLIST_LISTING_IDS } from '../../../ducks/user.duck';
import { formatMoney } from '../../../util/currency';
import { fetchActiveSales, getEffectivePrice, saleForListing, salePercentOff } from '../../../util/sales';
import { useIntl } from '../../../util/reactIntl';
import { createSlug } from '../../../util/urlHelpers';
import { isAcceptingOffers } from '../../../util/offers';

import css from './MarketplaceListingCard.module.css';

/**
 * Sale listing card used only by the normal Marketplace results route.
 *
 * @param {object} props
 * @param {object} props.listing Marketplace listing entity
 * @param {string} props.renderSizes responsive image source sizes
 * @returns {JSX.Element}
 */
const MarketplaceListingCard = ({ listing, renderSizes }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const intl = useIntl();
  const config = useConfiguration();
  const currentUser = useSelector(state => state.user?.currentUser);
  const wishlistUpdateInProgress = useSelector(state => state.user?.wishlistUpdateInProgress);
  const [sale, setSale] = useState(null);
  const wishlistListingIds = currentUser?.attributes?.profile?.privateData?.[WISHLIST_LISTING_IDS];
  const savedListingIds = Array.isArray(wishlistListingIds) ? wishlistListingIds : [];
  const attributes = listing?.attributes || {};
  const { title = '', price, publicData = {} } = attributes;
  const { variantPrefix = 'listing-card' } = config?.layout?.listingImage || {};
  const image = listing?.images?.[0];
  const variants = image
    ? Object.keys(image.attributes?.variants || {}).filter(name => name.startsWith(variantPrefix))
    : [];
  const slug = createSlug(title);
  const isSaved = savedListingIds.includes(listing.id.uuid);
  const formattedPrice = price ? formatMoney(intl, price) : null;
  const author = publicData.author || publicData.bookAuthor;
  const edition = publicData.editionsource || publicData.edition || publicData.retailer;
  const signed = publicData.signed || publicData.signedpreference;
  const condition = publicData.condition;
  const badges = [edition, signed, condition].filter(Boolean).slice(0, 3);
  const listingOrigin = {
    state: { from: { pathname: location.pathname, search: location.search }, backLabel: 'Back to Marketplace' },
  };

  useEffect(() => {
    let mounted = true;
    fetchActiveSales([listing?.id?.uuid]).then(sales => {
      if (mounted) setSale(saleForListing(sales, listing?.id?.uuid));
    });
    return () => {
      mounted = false;
    };
  }, [listing?.id?.uuid]);

  const salePrice = sale ? getEffectivePrice(listing, sale) : null;
  const discount = sale ? salePercentOff(price, sale) : null;

  return (
    <article className={css.card}>
      <div className={css.imageFrame}>
        <NamedLink className={css.imageLink} name="ListingPage" params={{ id: listing.id.uuid, slug }} to={listingOrigin}>
          <ResponsiveImage
            rootClassName={css.image}
            alt={title}
            image={image}
            variants={variants}
            sizes={renderSizes}
          />
        </NamedLink>
        <WishlistHeart
          className={css.heartButton}
          isSaved={isSaved}
          inProgress={wishlistUpdateInProgress}
          onToggle={() => dispatch(updateWishlist({ listingId: listing.id.uuid, isSaved: !isSaved }))}
        />
        {isAcceptingOffers(publicData.acceptOffers) ? <span className={css.offersBadge}>Offers welcome</span> : null}
      </div>
      <div className={css.info}>
        <NamedLink className={css.title} name="ListingPage" params={{ id: listing.id.uuid, slug }} to={listingOrigin}>
          {title}
        </NamedLink>
        {author ? <div className={css.author}>{author}</div> : null}
        {badges.length > 0 ? (
          <div className={css.badges}>
            {badges.map(badge => (
              <span key={badge}>{badge}</span>
            ))}
          </div>
        ) : null}
        {salePrice && discount ? (
          <div className={css.price}>
            <span className={css.saleBadge}>{discount}% OFF</span>
            <span className={css.originalPrice}>{formattedPrice}</span>
            <span className={css.salePrice}>{formatMoney(intl, salePrice)}</span>
          </div>
        ) : formattedPrice ? (
          <div className={css.price}>{formattedPrice}</div>
        ) : null}
      </div>
    </article>
  );
};

export default MarketplaceListingCard;
